import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";

export const CartContext = createContext(null);

const readStorage = (key, fallback) => {
  if (typeof window === "undefined") return fallback;

  try {
    const storedValue = window.localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : fallback;
  } catch {
    return fallback;
  }
};

const persistStorage = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Browsers can disable storage; the in-memory shopping flow still works.
  }
};

const isSameLine = (item, productId, variant, color) =>
  Number(item.id) === Number(productId) &&
  item.size === variant &&
  item.color === color;

const getInventoryLimit = (productId) => {
  const product = productData.find(
    (item) => Number(item.id) === Number(productId)
  );
  const inventory = Number(product?.inventory);
  return Number.isFinite(inventory) ? Math.max(0, inventory) : Infinity;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStorage("hushcider-cart", [])
  );
  const [wishlist, setWishlist] = useState(() =>
    readStorage("hushcider-wishlist", [])
  );

  useEffect(() => persistStorage("hushcider-cart", cartItems), [cartItems]);
  useEffect(
    () => persistStorage("hushcider-wishlist", wishlist),
    [wishlist]
  );

  const addToCart = useCallback(
    (productId, quantity = 1, variant = "Standard", color = "Default") => {
      const safeQuantity = Math.max(1, Number(quantity) || 1);
      const inventoryLimit = getInventoryLimit(productId);

      setCartItems((items) => {
        if (inventoryLimit === 0) return items;
        const existingItem = items.find((item) =>
          isSameLine(item, productId, variant, color)
        );

        if (!existingItem) {
          return [
            ...items,
            {
              id: Number(productId),
              size: variant,
              color,
              quantity: Math.min(safeQuantity, inventoryLimit),
            },
          ];
        }

        return items.map((item) =>
          isSameLine(item, productId, variant, color)
            ? {
                ...item,
                quantity: Math.min(
                  item.quantity + safeQuantity,
                  inventoryLimit
                ),
              }
            : item
        );
      });
    },
    []
  );

  const removeFromCart = useCallback((productId, variant, color) => {
    setCartItems((items) =>
      items.filter(
        (item) => !isSameLine(item, productId, variant, color)
      )
    );
  }, []);

  const updateQuantity = useCallback(
    (productId, variant, color, quantity) => {
      const nextQuantity = Number(quantity);

      if (nextQuantity <= 0) {
        removeFromCart(productId, variant, color);
        return;
      }

      const inventoryLimit = getInventoryLimit(productId);
      if (inventoryLimit === 0) {
        removeFromCart(productId, variant, color);
        return;
      }

      setCartItems((items) =>
        items.map((item) =>
          isSameLine(item, productId, variant, color)
            ? {
                ...item,
                quantity: Math.min(
                  inventoryLimit,
                  Math.max(1, nextQuantity)
                ),
              }
            : item
        )
      );
    },
    [removeFromCart]
  );

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productId) => {
    setWishlist((items) => {
      const normalizedId = Number(productId);
      return items.includes(normalizedId) ? items : [...items, normalizedId];
    });
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    setWishlist((items) =>
      items.filter((id) => Number(id) !== Number(productId))
    );
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const normalizedId = Number(productId);
    setWishlist((items) =>
      items.includes(normalizedId)
        ? items.filter((id) => id !== normalizedId)
        : [...items, normalizedId]
    );
  }, []);

  const value = useMemo(
    () => ({
      cartItems,
      wishlist,
      itemCount: cartItems.reduce((total, item) => total + item.quantity, 0),
      wishlistCount: wishlist.length,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted: (productId) => wishlist.includes(Number(productId)),
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlist,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
